import { CircleSlash, EyeOff, Lock, TriangleAlert } from 'lucide-react'
import { useRef, type KeyboardEvent, type ReactNode } from 'react'

import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Pesewas } from '@/lib/money'
import { cn } from '@/lib/utils'

import { COLUMN_SHORT_LABELS, type CollectionColumn } from './constants'
import {
  amountKey,
  readAmount,
  type SheetAmounts,
  type SheetCell,
  type SheetRow,
} from './sheet-model'
import type { LineResult } from './use-batch-poster'

/**
 * The collection grid.
 *
 * This is the highest-volume screen in the release and it is worked with two hands on a keyboard,
 * so the movement model is the design rather than a garnish:
 *
 * - **Down the column, not across the row.** A paper sheet is totalled by column and is read down
 *   one, so Enter and Arrow Down move to the same column of the next member. Tab still moves
 *   across a row, because that is what Tab means everywhere else and overriding it would break
 *   every other way into the grid.
 * - **Past the empties.** A member with no live round has no field in that column. Moving down
 *   skips to the next member who does have one instead of stopping at a cell that cannot be typed
 *   into.
 * - **Off the end, onto the next column.** Enter at the bottom of a column lands at the top of the
 *   next, which is the order the columns are totalled in.
 *
 * Nothing is autofocused, and there is no arrow-key stepping inside a money field — a stray key
 * must never change an amount somebody has already counted at the counter.
 */

const CELL_ATTRIBUTE = 'data-sheet-cell'

export interface CollectionGridProps {
  rows: SheetRow[]
  columns: CollectionColumn[]
  amounts: SheetAmounts
  onAmountChange: (customerId: string, column: CollectionColumn, value: Pesewas | null) => void
  /** Per-line outcomes once a batch has run. A posted line is locked. */
  results: Record<string, LineResult>
  /** Lines this browser already posted in an earlier sitting, from the sheet's journal. */
  alreadyPosted: Record<string, { amountMinor: number; reference: string }>
  disabled: boolean
  /** Line keys the sheet knows the server will refuse, so the field can be marked before posting. */
  objectionKeys: Set<string>
}

export function CollectionGrid({
  rows,
  columns,
  amounts,
  onAmountChange,
  results,
  alreadyPosted,
  disabled,
  objectionKeys,
}: CollectionGridProps) {
  const gridRef = useRef<HTMLDivElement | null>(null)

  /**
   * Moves focus down or up a column, skipping members who have no field in it.
   *
   * The search is over the rendered inputs rather than over the model, so a column hidden by
   * permission or a member whose account could not be read simply is not there to land on.
   */
  const moveWithinColumn = (fromRow: number, column: number, step: 1 | -1) => {
    const container = gridRef.current
    if (!container) {
      return false
    }

    for (let row = fromRow + step; row >= 0 && row < rows.length; row += step) {
      const next = container.querySelector<HTMLInputElement>(
        `[${CELL_ATTRIBUTE}="${String(row)}:${String(column)}"]`,
      )
      if (next) {
        next.focus()
        next.select()
        return true
      }
    }

    return false
  }

  /** The first field in a column, for the wrap off the bottom of the previous one. */
  const focusColumnTop = (column: number) => {
    const container = gridRef.current
    if (!container) {
      return
    }

    for (let row = 0; row < rows.length; row += 1) {
      const candidate = container.querySelector<HTMLInputElement>(
        `[${CELL_ATTRIBUTE}="${String(row)}:${String(column)}"]`,
      )
      if (candidate) {
        candidate.focus()
        candidate.select()
        return
      }
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null
    const cell = target?.getAttribute(CELL_ATTRIBUTE)
    if (!cell) {
      return
    }

    const [rawRow, rawColumn] = cell.split(':')
    const row = Number(rawRow)
    const column = Number(rawColumn)

    if (!Number.isInteger(row) || !Number.isInteger(column)) {
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      event.preventDefault()
      const moved = moveWithinColumn(row, column, 1)
      // Off the bottom of a column: on to the top of the next one, which is the order the columns
      // are totalled in. Off the bottom of the last column, focus simply stays put — the operator
      // is finished and the declared total is the next tab stop.
      if (!moved && event.key === 'Enter' && column + 1 < columns.length) {
        focusColumnTop(column + 1)
      }
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveWithinColumn(row, column, -1)
    }
  }

  const columnTotal = (column: CollectionColumn): Pesewas =>
    rows.reduce((total, row) => total + (readAmount(amounts, row.customerId, column) ?? 0), 0)

  return (
    <div ref={gridRef} onKeyDown={onKeyDown} className="rounded-card border border-border-subtle bg-surface">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 text-right">Line</TableHead>
            <TableHead>Member</TableHead>
            <TableHead className="w-28">Register</TableHead>
            {columns.map((column) => (
              <TableHead key={column} className="w-64 text-right">
                {COLUMN_SHORT_LABELS[column]}
              </TableHead>
            ))}
            <TableHead className="w-36 text-right">Line total</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row, rowIndex) => {
            const lineTotal = columns.reduce(
              (total, column) => total + (readAmount(amounts, row.customerId, column) ?? 0),
              0,
            )

            return (
              <TableRow key={row.customerId} className="align-top">
                <TableCell className="numeric text-right text-xs text-muted tabular-nums">
                  {row.lineNumber}
                </TableCell>

                <TableCell>
                  <p className="text-sm font-medium text-heading">{row.fullName}</p>
                  <p className="font-mono text-2xs text-muted">{row.customerNumber}</p>
                </TableCell>

                <TableCell>
                  {row.attendance === null ? (
                    <span className="text-2xs text-muted">Not recorded</span>
                  ) : (
                    <Badge
                      tone={
                        row.attendance === 'Absent'
                          ? 'danger'
                          : row.attendance === 'Excused'
                            ? 'neutral'
                            : 'success'
                      }
                    >
                      {row.attendance}
                    </Badge>
                  )}
                </TableCell>

                {columns.map((column, columnIndex) => (
                  <TableCell key={column}>
                    <GridCell
                      cell={row.cells[column]}
                      cellId={`${String(rowIndex)}:${String(columnIndex)}`}
                      memberName={row.fullName}
                      columnLabel={COLUMN_SHORT_LABELS[column]}
                      value={readAmount(amounts, row.customerId, column)}
                      onChange={(value) => onAmountChange(row.customerId, column, value)}
                      result={results[amountKey(row.customerId, column)]}
                      previouslyPosted={alreadyPosted[amountKey(row.customerId, column)]}
                      objected={objectionKeys.has(amountKey(row.customerId, column))}
                      disabled={disabled}
                    />
                  </TableCell>
                ))}

                <TableCell>
                  <MoneyDisplay
                    value={lineTotal === 0 ? null : lineTotal}
                    emphasis={lineTotal === 0 ? 'muted' : 'strong'}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell colSpan={3} className="text-xs font-semibold text-heading uppercase">
              Column totals
            </TableCell>
            {columns.map((column) => (
              <TableCell key={column}>
                <MoneyDisplay value={columnTotal(column)} emphasis="strong" />
              </TableCell>
            ))}
            <TableCell>
              <MoneyDisplay
                value={columns.reduce((total, column) => total + columnTotal(column), 0)}
                emphasis="strong"
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  )
}

/**
 * One cell.
 *
 * A cell with nowhere to post says which of the four reasons applies, in words, rather than
 * rendering an empty box. The four are not interchangeable — see {@link SheetCell} — and telling
 * an officer "no loan" when the truth is "you may not read loans" sends a member home unpaid.
 */
function GridCell({
  cell,
  cellId,
  memberName,
  columnLabel,
  value,
  onChange,
  result,
  previouslyPosted,
  objected,
  disabled,
}: {
  cell: SheetCell
  cellId: string
  memberName: string
  columnLabel: string
  value: Pesewas | null
  onChange: (value: Pesewas | null) => void
  result: LineResult | undefined
  previouslyPosted: { amountMinor: number; reference: string } | undefined
  objected: boolean
  disabled: boolean
}) {
  if (cell.availability.kind === 'restricted') {
    return (
      <CellNotice icon={<EyeOff className="size-3.5" aria-hidden />} tone="muted">
        Withheld — your role does not hold{' '}
        <span className="font-mono">{cell.availability.permission}</span>. This is not a statement
        that there is nothing here.
      </CellNotice>
    )
  }

  if (cell.availability.kind === 'pending') {
    return <Skeleton className="h-9 w-full" />
  }

  if (cell.availability.kind === 'unreadable') {
    return (
      <CellNotice icon={<TriangleAlert className="size-3.5" aria-hidden />} tone="danger">
        Could not be read. Retry the sheet before posting against this member.
      </CellNotice>
    )
  }

  if (cell.availability.kind === 'none') {
    return (
      <CellNotice icon={<CircleSlash className="size-3.5" aria-hidden />} tone="muted">
        {cell.availability.explanation}
      </CellNotice>
    )
  }

  if (cell.availability.kind === 'blocked') {
    return (
      <CellNotice icon={<TriangleAlert className="size-3.5" aria-hidden />} tone="warning">
        {cell.availability.explanation}
      </CellNotice>
    )
  }

  if (cell.availability.kind === 'readOnly') {
    return (
      <CellNotice icon={<Lock className="size-3.5" aria-hidden />} tone="muted">
        {cell.target ? `${cell.target.reference} · ` : null}
        Posting needs <span className="font-mono">{cell.availability.permission}</span>.
      </CellNotice>
    )
  }

  const target = cell.target
  if (!target) {
    return null
  }

  const posted = result?.status === 'posted' ? result : null
  const refused = result?.status === 'refused' ? result : null
  const locked = disabled || posted !== null || previouslyPosted !== undefined

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-2xs text-muted">{target.reference}</span>
        <MoneyDisplay
          value={target.contextMinor}
          symbol={false}
          emphasis="muted"
          className="text-2xs"
        />
      </div>

      <MoneyInput
        value={value}
        onChange={onChange}
        currency={target.currencyCode}
        disabled={locked}
        aria-label={`${columnLabel} for ${memberName}`}
        aria-invalid={objected || refused !== null}
        data-sheet-cell={cellId}
        className={cn(objected && 'border-danger-fill')}
      />

      {target.arrearsMinor !== null && target.arrearsMinor > 0 ? (
        <p className="text-2xs text-danger-text">
          In arrears{' '}
          {target.daysInArrears === null ? null : `${String(target.daysInArrears)} days · `}
          {/* Shown beside the field, because it is the figure an officer is chasing at the meeting. */}
          <MoneyDisplay
            value={target.arrearsMinor}
            symbol={false}
            className="inline text-2xs"
          />
        </p>
      ) : null}

      {previouslyPosted ? (
        <p className="text-2xs text-success-text">
          Already posted in this sitting · <span className="font-mono">{previouslyPosted.reference}</span>
        </p>
      ) : posted ? (
        <p className="text-2xs text-success-text">
          {posted.wasReplay ? 'Already posted' : 'Posted'} ·{' '}
          <span className="font-mono">{posted.reference}</span>
        </p>
      ) : refused ? (
        <p className="text-2xs text-danger-text">
          Refused · <span className="font-mono">{refused.error.code || 'no code'}</span>
        </p>
      ) : null}
    </div>
  )
}

function CellNotice({
  icon,
  tone,
  children,
}: {
  icon: ReactNode
  tone: 'muted' | 'warning' | 'danger'
  children: ReactNode
}) {
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-2xs',
        tone === 'muted' && 'text-muted',
        tone === 'warning' && 'text-warning-text',
        tone === 'danger' && 'text-danger-text',
      )}
    >
      <span className="mt-px shrink-0">{icon}</span>
      <span>{children}</span>
    </p>
  )
}
